import type { PropsWithChildren } from 'react';
import type { AccessorFn, CellContext } from '@tanstack/react-table';
import { useWriteGatedInteraction } from '@interxfi/design-system/access-context';
import { type Interaction, Interactive } from '@interxfi/design-system/util';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import DisclosureRightIcon from '../../../../icons/react/icon-disclosure-right';
import MoreIcon from '../../../../icons/react/icon-more';
import { PlainIconButton } from '../../../button/plain-icon/plain-icon-button';
import { useTableRowContext } from '../../row-context';
import type { TableColumnDef } from '../column';
import styles from './row-action.module.scss';

const baseColumnDefinition: Partial<TableColumnDef<any>> = {
  align: 'center',
  header: '',
  enableSorting: false,
  enableMultiSort: false,
  enableHiding: false,
  enableColumnFilter: false,
  enableGlobalFilter: false,
  enableGrouping: false,
  enablePinning: false,
  enableResizing: false,
  maxSize: 32,
  minSize: 32,
  size: 32,
};

export function RowActionWrapper({ children }: PropsWithChildren) {
  return <div className={styles.rowAction}>{children ?? <DisclosureRightIcon aria-hidden height={16} />}</div>;
}

function RowLevelInteraction({ interaction: providedInteraction }: { interaction: Interaction | undefined }) {
  const interaction = useWriteGatedInteraction(providedInteraction);

  switch (interaction?.type) {
    case 'drawer': {
      return (
        <RowActionWrapper>
          <Interactive {...interaction}>
            {interaction.drawer.props?.trigger ?? (
              <PlainIconButton aria-label="Expand row details">
                <DisclosureRightIcon aria-hidden height={16} />
              </PlainIconButton>
            )}
          </Interactive>
        </RowActionWrapper>
      );
    }
    case 'modal':
      return (
        <RowActionWrapper>
          <Interactive {...interaction}>
            {interaction.modal.props?.trigger ?? (
              <PlainIconButton aria-label="Expand row details">
                <DisclosureRightIcon aria-hidden height={16} />
              </PlainIconButton>
            )}
          </Interactive>
        </RowActionWrapper>
      );
    case 'link': {
      return (
        <RowActionWrapper>
          <Interactive {...interaction}>
            {interaction.link.children ?? (
              <PlainIconButton aria-label="Expand row details">
                <DisclosureRightIcon aria-hidden height={16} />
              </PlainIconButton>
            )}
          </Interactive>
        </RowActionWrapper>
      );
    }
    case 'button': {
      return (
        <RowActionWrapper>
          <Interactive {...interaction}>
            {interaction.button.children ?? (
              <PlainIconButton aria-label="Expand row details">
                <DisclosureRightIcon aria-hidden height={16} />
              </PlainIconButton>
            )}
          </Interactive>
        </RowActionWrapper>
      );
    }
    case 'dropdownMenu': {
      return (
        <Interactive {...interaction}>
          <PlainIconButton>
            <MoreIcon aria-hidden width={16} />
          </PlainIconButton>
        </Interactive>
      );
    }
    case 'styledButton': {
      return (
        <RowActionWrapper>
          <Interactive {...interaction}>
            {interaction.styledButton.children ?? (
              <PlainIconButton aria-label="Interact with row">
                <DisclosureRightIcon aria-hidden height={16} />
              </PlainIconButton>
            )}
          </Interactive>
        </RowActionWrapper>
      );
    }
    case 'styledButtonLink': {
      return (
        <RowActionWrapper>
          <Interactive {...interaction}>
            {interaction.styledButtonLink.children ?? (
              <PlainIconButton aria-label="Interact with row">
                <DisclosureRightIcon aria-hidden height={16} />
              </PlainIconButton>
            )}
          </Interactive>
        </RowActionWrapper>
      );
    }
    case 'tooltip':
    case undefined:
      return null;
    default: {
      exhaustiveCheck(interaction);
      return null;
    }
  }
}

export function BaseRowInteractionCell<TData, TValue extends Interaction | undefined>({ getValue }: CellContext<TData, TValue>) {
  const rowLevelInteraction = getValue<Interaction | undefined>();
  if (!rowLevelInteraction) {
    return null;
  }

  return <RowLevelInteraction interaction={rowLevelInteraction} />;
}

function RowInteractionCell() {
  const { rowLevelInteraction } = useTableRowContext();

  return <RowLevelInteraction interaction={rowLevelInteraction} />;
}

export function getRowInteraction<T extends object>(options?: Partial<TableColumnDef<T>>): TableColumnDef<T> {
  return {
    id: 'rowLevelInteraction',
    ...baseColumnDefinition,
    isActionColumn: true,
    pin: true,
    enablePinning: true,
    accessorFn: () => '',
    cell: RowInteractionCell,
    ...options,
  };
}

export function getInteractionCell<T extends object>(
  accessorFn: AccessorFn<T, Interaction | undefined>,
  options?: Partial<TableColumnDef<T, Interaction | undefined>>,
): TableColumnDef<T, Interaction | undefined> {
  return {
    ...getRowInteraction(options),
    id: 'interactionCell',
    accessorFn,
    cell: BaseRowInteractionCell,
  };
}
